import { Component, inject, output, signal, ViewChild } from '@angular/core';
import { MaterialModule } from '../../../../modules/material.module';
import { environment } from '../../../../../environments/environment';
import { AccountDataService } from '../../account-data.service';
import {
  MatDialog,
  MatDialogRef,
} from '@angular/material/dialog';
import { ChangePasswordDialogComponent } from '../change-password-dialog/change-password-dialog.component';
import { DeleteAccountDialogComponent } from '../delete-account-dialog/delete-account-dialog.component';
import {
  StripeCardElementOptions,
  StripeElementsOptions,
} from '@stripe/stripe-js';
import {
  StripeCardComponent,
  StripeFactoryService,
  StripeInstance,
} from 'ngx-stripe';
import { AccountService } from '../../../../shared/services/account.service';
import { DASHBOARD_THEMES, THEME_NAMES } from '../utils/constant';
import { HttpQueueService } from '../../../../shared/services/http-queue.service';
import { ProcessQueueRequestAction } from '../../../../shared/utils/enums';

@Component({
  selector: 'app-account-settings-dialog',
  standalone: true,
  imports: [MaterialModule, StripeCardComponent],
  templateUrl: './account-settings-dialog.component.html',
  styleUrl: './account-settings-dialog.component.scss',
})
export class AccountSettingsDialogComponent {
  private readonly ac = inject(AccountDataService);
  private readonly dialog = inject(MatDialog);
  readonly dialogRef = inject(MatDialogRef<AccountSettingsDialogComponent>);
  protected accountService = inject(AccountService);
  private httpQueueService = inject(HttpQueueService);

  @ViewChild(StripeCardComponent) stripeCredCard: StripeCardComponent;

  creditCardElement = signal<boolean>(false);
  proActivationButton = signal({
    showWrapper: false,
    text: 'Upgrade to Pro',
    disabled: false,
  });
  isDeactivateLinkWrapper = signal(false);
  dashboardTheme: string; // the style theme of this dashboard (e.g., 'defaultTheme', 'seaTheme', 'nightSkyTheme')
  credCardFormErrors = signal<string>('');
  credCardText = signal<string>('***** ****** *****');
  expirationNotice = signal({
    showWrapper: false,
    text: '',
  });
  isCancelLinkWrapper = signal<boolean>(false);
  updateCredCardDetailsButton = signal({
    showWrapper: false,
    disabled: false,
  });

  THEME_NAMES = THEME_NAMES;
  protected dashboardThemeOptions: Array<{
    name: string;
    title: string;
    image: string;
    dashboardContentClass: string;
  }> = DASHBOARD_THEMES; // the style theme of this dashboard (e.g., 'defaultTheme', 'seaTheme', 'nightSkyTheme')

  cardOptions: StripeCardElementOptions = {
    style: {
      base: {
        iconColor: '#666EE8',
        color: '#31325F',
        fontWeight: '300',
        fontFamily: '"Helvetica Neue", Helvetica, sans-serif',
        fontSize: '18px',
      },
    },
  };

  elementsOptions: StripeElementsOptions = {
    locale: 'en',
  };

  stripe: StripeInstance;

  constructor(private stripeFactory: StripeFactoryService) {}

  ngOnInit() {
    this.stripe = this.stripeFactory.create(environment.stripePublishableKey);

    // set appropriate display in pro account settings dialog box
    if (!this.accountService.subscription().isProAccount) {
      this.setNonProAccountDisplay();
    } else {
      if (this.accountService.subscription().proExpiryDate) {
        this.setProAccountSetToExpireDisplay();
      } else {
        this.setProAccountAutoRenewDisplay();
      }
    }
  }

  // display applicable errors, else generate a temporary token corresponding to
  // the input credit card and send it to the server such to upgrade sql backend
  // and stripe api to show account is upgraded. then display appropriate
  // content to user
  handleProPaymentFormSubmit() {
    this.stripe.createToken(this.stripeCredCard.element).subscribe({
      next: (result) => {
        if (result.token) {
          // disable form submit button
          this.proActivationButton.update((state) => ({
            ...state,
            disabled: true,
          }));

          // insert the temporary credit card token into the form so it gets
          // submitted to the server
          this.insertCcTokenIntoForm(result.token.id);
          this.ac
            .upgradeAccountToPro({ creditCardToken: result.token.id })
            .subscribe({
              next: (response) => {
                let result = response['result'];
                let lastFourDigits = response['lastFourDigits'];

                this.accountService.subscription.update((state) => {
                  state.isProAccount = true;
                  state.proExpiryDate = null;
                  state.credCardLastFourDigits = lastFourDigits;
                  return state;
                });

                if (environment.debuggingConsoleLogsOn) {
                  console.log(result);
                }
                this.setProAccountAutoRenewDisplay();
              },
              error: (error) => {
                console.log(error);
              }
            });
        } else if (result.error) {
          // Error creating the token
          this.credCardFormErrors.set(result.error.message);
        }
      },
    });
  }

  // show appropriate error message if applicable, else generate a temporary
  // token associated with the newly input credit card details and send it to
  // the server such to update both backend sql database and stripe api to show
  // updated credit card details. then display appropriate dashboard content.
  handleUpdateCardDetailsFormSubmit(): void {
    // create a temporary token or display an error when the form is submitted
    this.stripe.createToken(this.stripeCredCard.element).subscribe({
      next: (result) => {
        if (result.error) {
          this.credCardFormErrors.set(result.error.message);
        } else {
          // disable form submit button
          this.updateCredCardDetailsButton.update((state) => ({
            ...state,
            disabled: true,
          }));

          // insert the temporary credit card token into the form so it gets
          // submitted to the server
          this.insertCcTokenIntoForm(result.token.id);

          // send data to backend to update credit card details for account
          this.ac
            .updateCredCardDetails({ creditCardToken: result.token.id })
            .subscribe({
              next: (response) => {
                let result = response['result'];
                let lastFourDigits = response['lastFourDigits'];

                this.accountService.subscription.update((state) => {
                  state.credCardLastFourDigits = lastFourDigits;
                  return state;
                });
                if (environment.debuggingConsoleLogsOn) {
                  console.log(result);
                }
                this.setProAccountAutoRenewDisplay();
              },
              error: (error) => {
                console.log(error);
              },
            });
        }
      },
    });
  }

  // reset to main pro account display again if press cancel on update credit
  // card details form
  cancelLinkHandler(): void {
    this.setProAccountAutoRenewDisplay();
  }

  // set various content and styling to indicate that this account has an
  // activated Pro subscription
  setProAccountAutoRenewDisplay(): void {
    // show appropriate content in "Pro settings" dialog box and navigation bar
    this.credCardText.update((state) =>
      state.includes(this.accountService.subscription().credCardLastFourDigits)
        ? state
        : state.concat(this.accountService.subscription().credCardLastFourDigits)
    );
    this.creditCardElement.set(true);
    this.isDeactivateLinkWrapper.set(true);
    this.updateCredCardDetailsButton.update((state) => ({
      ...state,
      showWrapper: false,
    }));
    this.proActivationButton.set({
      showWrapper: true,
      text: 'Pro Account Activated',
      disabled: true,
    });
    this.expirationNotice.update((state) => ({ ...state, showWrapper: false }));
    this.isCancelLinkWrapper.set(false);
  }

  // display the appropriate screen for inputing new credit card details
  changeCredCardLinkHandler(): void {
    this.setUpdateCredCardDetailsDisplay();
  }

  // set appropriate content and styling for updating credit card details
  setUpdateCredCardDetailsDisplay(): void {
    this.creditCardElement.set(false);
    this.proActivationButton.set({
      showWrapper: false,
      text: 'Upgrade to Pro',
      disabled: false,
    });
    this.isDeactivateLinkWrapper.set(false);
    this.expirationNotice.update((state) => ({ ...state, showWrapper: false }));
    this.isCancelLinkWrapper.set(true);
    this.updateCredCardDetailsButton.set({
      showWrapper: true,
      disabled: false,
    });
  }

  // make appropriate call to backend to deactivate pro account and then
  // show correct display
  deactivateProLinkHandler(): void {
    this.ac.deactivateProForThisAccount().subscribe({
      next: (response) => {
        let result = response['result'];

        this.accountService.subscription.update((state) => {
          state.proExpiryDate = response['pro_expiry_date'];
          state.credCardLastFourDigits = '';
          return state;
        });

        if (environment.debuggingConsoleLogsOn) {
          console.log(result);
        }
        this.setProAccountSetToExpireDisplay();
      },
      error: (error) => {
        console.log(error);
      },
    });
  }

  // set various content and styling appropriate to an account that has recently
  // cancelled their Pro subscription and will have it expire in the near future
  setProAccountSetToExpireDisplay(): void {
    this.creditCardElement.set(false);
    this.expirationNotice.set({
      showWrapper: true,
      text:
        'This Pro account is set to expire on ' +
        this.accountService.subscription().proExpiryDate +
        ' UTC.',
    });
    this.isDeactivateLinkWrapper.set(false);
    this.proActivationButton.set({
      showWrapper: true,
      text: 'Reactivate Pro',
      disabled: false,
    });
    this.updateCredCardDetailsButton.update((state) => ({
      ...state,
      showWrapper: false,
    }));
    this.isCancelLinkWrapper.set(false);
  }

  setNonProAccountDisplay(): void {
    // show appropriate content in "Pro settings" dialog box and navigation bar
    this.creditCardElement.set(false);

    if (this.accountService.subscription().proExpiryDate) {
      this.expirationNotice.set({
        showWrapper: true,
        text: 'This Pro account expired on ' + this.accountService.subscription().proExpiryDate + ' UTC.',
      });
    }
    this.isDeactivateLinkWrapper.set(false);
    this.updateCredCardDetailsButton.update((state) => ({
      ...state,
      showWrapper: false,
    }));
    this.proActivationButton.set({
      showWrapper: true,
      text: 'Upgrade to Pro',
      disabled: false,
    });
  }

  // insert a given temporary credit card token into the form so it gets
  // submitted to the server
  insertCcTokenIntoForm(tokenId: string): void {
    let paymentForm = document.getElementById('paymentForm');
    let hiddenInput = document.createElement('input');
    hiddenInput.setAttribute('type', 'hidden');
    hiddenInput.setAttribute('name', 'stripeToken');
    hiddenInput.setAttribute('value', tokenId);
    paymentForm.appendChild(hiddenInput);
  }

  /**
   * triggered when user selects a new dashboard theme
   * given a dashboard theme selected by the user
   * set selected theme in accountService
   */
  dashboardThemeSelectAction(selectedTheme: string): void {
    this.httpQueueService.enqueueRequest(ProcessQueueRequestAction.SaveDashboardTheme);
    this.accountService.otherAccountSettings.update((state) => {
      state.dashboardTheme = this.dashboardThemeOptions.find(
        (theme) => theme.name === selectedTheme
      );
      return state;
    })
  }

  changeCardValues(event) {
    if (event.error) {
      this.credCardFormErrors.set(event.error.message);
    } else {
      this.credCardFormErrors.set('');
    }
  }

  showChangePasswordDialog() {
    this.onClose();
    this.dialog.open(ChangePasswordDialogComponent, {
      width: '400px',
    });
  }

  // display a modal dialog for delete account
  showDeleteAccountDialog(): void {
    this.onClose();
    this.dialog.open(DeleteAccountDialogComponent);
  }

  onClose(): void {
    this.dialogRef.close();
  }
}
